﻿import "@fiqas/utils";
import { IComponent, Component, Attribute, Input, Output, Inject } from "@fiqas/angular-utils"
import { FIQAssist } from "../../app"
import { Application } from "../application"
import { TableCell, TableRow, TableColumn } from "../../controls/datatable"
import {
    CompactServiceCase, CompactRecentServiceCase, ServiceCaseRef, FeedbackStatus,
    CompactSearchServiceCase
} from "../../models/serviceCase"
import { IDisposable } from "../../interfaces/general"
import { TranslationService } from "../../services/TranslationService"
import { CacheService } from "../../services/cache";
import { SessionService } from "../../services/session";
import { LogService } from "../../services/logService";
import { Customer } from "../../models/customer";
import { Person } from "../../models/person";
import { IPromise } from "angular";
import { IResource, IResourceResolver } from "../../interfaces/iFqs";
import { AuthorizationService } from "../../services/authorizationService";

@Component({
    module: FIQAssist,
    selector: 'servicecasetable',
    template: `<datatable fillaction-text="$ctrl.fillactionText" fillaction="$ctrl.fillactionPass()" storeid="$ctrl.storeid" sortcolumn="$ctrl.sortcolumn" noresult-text="$ctrl.noresultText"
                sortdescending="$ctrl.sortdescending" class="servicecasetable {{$ctrl.Mode}}" columns="$ctrl.Columns"
                data="$ctrl.Rows" onrowclicked="$ctrl.onRowClick(rowid, ctrlkey)" showtextnoresults="$ctrl.showtextnoresults"
                onsortdata="$ctrl.OnSortData(column,descending)" ondataresize="$ctrl.ondataresize(count)"></datatable>`,
    require: {
        "application": "^application"
    }

})
export class ServiceCaseTable implements IComponent {
    public application: Application;
    private _servicecases: CompactServiceCase[];
    private _showheaders: boolean;
    private initialized: boolean = false;

    private columnSortFunctions: { (x: CompactServiceCase, y: CompactServiceCase): number; }[];

    constructor(
        @Inject('$timeout') private $timeout: ng.ITimeoutService,
        @Inject('$window') private $window: ng.IWindowService,
        @Inject('$filter') private $filter: ng.IFilterService,
        @Inject() private cacheService: CacheService,
        @Inject() private sessionService: SessionService,
        @Inject() private translationService: TranslationService,
        @Inject() private authorizationService: AuthorizationService,
        @Inject('$rootRouter') private $rootRouter: ng.Router,
        @Inject() private logService: LogService
    ) {
    }

    @Input()
    get servicecases(): CompactServiceCase[] {
        return this._servicecases
    }

    set servicecases(servicecases: CompactServiceCase[]) {
        this._servicecases = servicecases;
        if (this.initialized && servicecases) {
            this.Sort(this.sortcolumn, this.sortdescending);
        }
    }

    @Input() public phone: boolean = false;
    @Input() public collapsed: boolean;
    @Input() public showemptyrow: boolean;
    @Attribute() public recentlayout: boolean;
    @Attribute() public referer: string;
    @Input() public storeid: string;
    @Input() public storedata: boolean;
    @Input() public storeglobalid: string;
    @Input() public showtextnoresults: boolean;
    @Attribute() public noresultText: string;
    @Attribute() public fillactionText: string;
    @Output() public fillaction: (args: {}) => void;
    private fillactionPass() {
        if (this.fillaction && this.fillactionText) this.fillaction({});
    }
    @Output() public onResultCountChanged: (args: { resultcount: number }) => void;

    private _sortcolumn: string;
    @Input() get sortcolumn(): string {
        return this._sortcolumn;
    }
    set sortcolumn(val: string) {
        this._sortcolumn = val;
    }

    private _defaultsortcolumn: string;
    @Attribute() get defaultsortcolumn(): string {
        return this._defaultsortcolumn;
    }
    set defaultsortcolumn(val: string) {
        this._defaultsortcolumn = val;
        if (this.sortdescending === undefined)
            this.sortcolumn = val;
    }
    private _forceMulti: boolean;
    @Attribute() public get forceMulti() {
        return !!this._forceMulti;
    }
    public set forceMulti(val: string | boolean) {
        this._forceMulti = val === 'true' || val === true;
    }

    private _sortdescending: boolean;
    @Input() get sortdescending(): boolean {
        return this._sortdescending
    }
    set sortdescending(val: boolean) {
        this._sortdescending = val;
    }

    private _defaultsortdescending: boolean;
    @Attribute() get defaultsortdescending() {
        return this._defaultsortdescending
    }
    set defaultsortdescending(val: boolean | string) {
        this._defaultsortdescending = val === true || val == "true";
        if (this.sortdescending === undefined)
            this.sortdescending = this._defaultsortdescending;
    }

    @Input()
    public get showheaders(): boolean {
        return this._showheaders;
    }
    public set showheaders(show: boolean) {
        let o = this._showheaders;
        this._showheaders = show;
        if (o != show && this.initialized) {
            this.MakeHeaders();
        }
    }

    private Rows: TableRow[];
    private Columns: TableColumn[];

    private buildSortList() {
        this.columnSortFunctions = [];
        this.columnSortFunctions.push((x, y) => {
            return x.type > y.type ? 1 : x.type == y.type ? x.severity > y.severity ? 1 : -1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return x.priority > y.priority ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return x.actionRequired > y.actionRequired ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return (x.feedbackStatus == "requested") > (y.feedbackStatus == "requested") ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return +x.caseNumber > +y.caseNumber ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return +x.parentCaseNumber > +y.parentCaseNumber ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return x.customerDesc.toLowerCase() > y.customerDesc.toLowerCase() ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return this.translationService.Translate(x.status) > this.translationService.Translate(y.status) ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return x.title.toLowerCase() > y.title.toLowerCase() ? 1 : -1;
        });
        this.columnSortFunctions.push((x, y) => {
            return x.createdBy.toLowerCase() > y.createdBy.toLowerCase() ? 1 : -1;
        });
        if (this.recentlayout) {
            this.columnSortFunctions.push((x: CompactRecentServiceCase, y: CompactRecentServiceCase) => {
                return new Date(x.lastOpened).getTime() - new Date(y.lastOpened).getTime();
            });
        }
        if (!this.recentlayout) {
            this.columnSortFunctions.push((x: CompactSearchServiceCase, y: CompactSearchServiceCase) => {
                return new Date(x.modifiedOn).getTime() - new Date(y.modifiedOn).getTime();
            });
        }
        this.columnSortFunctions.push((x: CompactRecentServiceCase, y: CompactRecentServiceCase) => {
            return (Date.parseOrNull(y.requestedDeadline) == null) ? -1 :
                        (Date.parseOrNull(x.requestedDeadline) == null) ? 1 :
                            Date.compare(Date.parseOrNull(x.requestedDeadline), Date.parseOrNull(y.requestedDeadline))
        });
        this.columnSortFunctions.push((x: CompactRecentServiceCase, y: CompactRecentServiceCase) => {
            return (Date.parseOrNull(y.rememberMeDate) == null) ? -1 :
                        (Date.parseOrNull(x.rememberMeDate) == null) ? 1 :
                            Date.compare(Date.parseOrNull(x.rememberMeDate), Date.parseOrNull(y.rememberMeDate))
        });
    };

    private disposables: IDisposable[] = [];

    $onInit() {
        this.initialized = true;
        this.MakeHeaders();
        if (this.storeid) {
            let sortcolumn = this.cacheService.getValue<string>("datatable." + this.storeid + ".sortcolumn");
            let sortdescending = this.cacheService.getValue<boolean>("datatable." + this.storeid + ".sortdescending");
            let Columns = this.cacheService.getValue<TableColumn[]>("datatable." + this.storeid + ".columns");

            if (sortcolumn === undefined)
                sortcolumn = this.sessionService.getValue<string>("datatable." + this.storeid + ".sortcolumn");
            if (sortdescending === undefined)
                sortdescending = this.sessionService.getValue<boolean>("datatable." + this.storeid + ".sortdescending");
            if (Columns === undefined)
                Columns = this.sessionService.getValue<TableColumn[]>("datatable." + this.storeid + ".columns");

            if (sortcolumn !== undefined)
                this.sortcolumn = sortcolumn;
            if (sortdescending !== undefined)
                this.sortdescending = sortdescending;
            if (Columns !== undefined)
                this.Columns = Columns;

            if (this.storedata && (!this.servicecases || this.servicecases.length == 0))
                this.servicecases = this.cacheService.getValue<CompactServiceCase[]>("datatable." + this.storeid + ".data");
        }
        if (this.servicecases && this.servicecases.length > 0)
            this.Sort(this.sortcolumn, this.sortdescending);
    }

    $onDestroy() {
        if (this.storeid && this.initialized) {
            this.cacheService.setValue("datatable." + this.storeid + ".sortcolumn", this.sortcolumn);
            this.cacheService.setValue("datatable." + this.storeid + ".sortdescending", this.sortdescending);
            this.cacheService.setValue("datatable." + this.storeid + ".columns", this.Columns);
            if (this.storedata)
                this.cacheService.setValue("datatable." + this.storeid + ".data", this.servicecases);

            this.sessionService.setValue("datatable." + this.storeid + ".sortcolumn", this.sortcolumn, false, false);
            this.sessionService.setValue("datatable." + this.storeid + ".sortdescending", this.sortdescending, false, false);
            this.sessionService.setValue("datatable." + this.storeid + ".columns", this.Columns, false, false);
        }
        this.disposables.forEach(d => {
            try { d() } catch (e) { this.logService.warning('ServiceCaseTable', 'Error disposing', e); }
        });
    }

    public onRowClick(rowid: any, withCtrl: boolean) {
        if (this.storeglobalid) {
            let casedata = this.Rows.map<ServiceCaseRef>(r => {
                let sc = this._servicecases.filter(c => c.id.href == r.id).shift();
                return { id: sc.id, number: sc.caseNumber, type: sc.type, title: sc.title };
            });
            this.cacheService.setValue("servicecases." + this.storeglobalid + ".data", casedata);
        }
        // Find object to see what type it is.
        this._servicecases.filter(x => x.id.href == rowid).forEach(x => {
            let parameters = { number: x.caseNumber };
            if (this.referer)
                parameters['?referer'] = this.referer;
            if (x.type == 'bug') {
                // Navigate to the edit page for this one.
                if (!withCtrl)
                    this.$rootRouter.navigate(['Problem', 'Edit', parameters]);
                else {
                    // double because it doesnt focus the first time
                    this.openUrlInNewWindow(`/problem/edit/${x.caseNumber}${(this.referer ? `?referer=${this.referer}` : '')}`, `/problem/edit/${x.caseNumber}${(this.referer ? `?referer=${this.referer}` : '')}`);
                }
            } else if (x.type == 'request') {
                // Navigate to the edit page for this one.
                if (!withCtrl)
                    this.$rootRouter.navigate(['Request', 'Edit', parameters]);
                else {
                    this.openUrlInNewWindow(`/request/edit/${x.caseNumber}${(this.referer ? `?referer=${this.referer}` : '')}`, `/request/edit/${x.caseNumber}${(this.referer ? `?referer=${this.referer}` : '')}`);
                }
            }
        });
    }

    private openUrlInNewWindow(url, name) {
        let w = window.open(url, name);
        w.focus();
    }

    public Sort(column: string, descending: boolean) {
        if (this.Columns && this.sortcolumn && this.sortdescending !== undefined) {
            for (let col = 0; col < this.Columns.length; col++) {
                if (this.Columns[col].key == this.sortcolumn) {
                    this.OnSortData(col, descending);
                }
            }
        }
    }

    public OnSortData(column: number, descending: boolean) {
        if (this._servicecases) {
            this.buildSortList();
            if (descending) {
                this._servicecases = this._servicecases.sort(this.columnSortFunctions[column]).reverse();
            } else {
                this._servicecases = this._servicecases.sort(this.columnSortFunctions[column]);
            }
            this.RefreshResult();
        }
    }

    public get Mode(): "mono" | "multi" {
        let sel = this.application.selectedCustomers;
        return (this.forceMulti || sel != undefined && sel.length > 1) ? 'multi' : 'mono';
    };

    public MakeHeaders() {
        if (this._showheaders) {
            let headers = [];
            headers.push(new TableColumn('casetype', 'CASE_TYPE', true, true));
            headers.push(new TableColumn('priority', '!', true, false));
            headers.push(new TableColumn('actionrequired', 'A', true, false));
            headers.push(new TableColumn('feedbackrequired', 'F', true, false));
            headers.push(new TableColumn('casenumber', 'CASE_NO', true, true));
            headers.push(new TableColumn('parentcasenumber', 'PARENT_NO', true, true));
            headers.push(new TableColumn('customer', 'CUSTOMER', true, true));
            headers.push(new TableColumn('status', 'STATUS', true, true));
            headers.push(new TableColumn('description', 'DESCRIPTION', true, true));
            headers.push(new TableColumn('issuedby', 'ISSUED_BY', true, true));
            if (this.recentlayout) {
                headers.push(new TableColumn('lastopened', 'LAST_OPENED', true, true));
                headers.push(new TableColumn('filler', '', true, true));
                if (this.authorizationService.currentRoleIn(["SD", "FQS"])) {
                    headers.push(new TableColumn('filler', '', true, true));
                }
            }
            if (!this.recentlayout) {
                headers.push(new TableColumn('recentopened', 'RECENT_OPENED', true, true));
                headers.push(new TableColumn('deadline', 'DEADLINE', true, true));
                if (this.authorizationService.currentRoleIn(["SD", "FQS"])) {
                    headers.push(new TableColumn('remember', 'REMEMBER', true, true));
                }
            }

            this.Columns = headers;
        } else {
            this.Columns = [];
        }
    }

    public async RefreshResult() {
        this.MakeHeaders();

        let column: TableColumn = this.Columns.filter(x => x.isSortedAscending || x.isSortedDescending).shift();
        if (column != undefined) {
            this.sortdescending = column.isSortedDescending;
            this.sortcolumn = column.key;
        }
        if (this._servicecases != null && this._servicecases.length > 0) {
            this.Rows = await Promise.all(this._servicecases.map(async (item) => {
                let row = new TableRow(item.id.href, new Array<TableCell>());
                row.link = [item.type == 'bug' ? '/Problem' : '/Request', 'Edit', { number: item.caseNumber }];
                row.cells.push(new TableCell(this.makeTypeIcon(item.type, item.severity)));
                row.cells.push(new TableCell(this.makePriorityIcon(item.priority)));
                row.cells.push(new TableCell(this.makeActionIcon(item.actionRequired)));
                row.cells.push(new TableCell(this.makeFeedbackIcon(item.feedbackStatus)));
                let style = ["closed", "cancelled"].indexOf(item.status) > -1 ? "closed" : "";
                row.cells.push(new TableCell(item.caseNumber, false, () => style));
                row.cells.push(new TableCell(item.parentCaseNumber, false));
                row.cells.push(new TableCell((this.phone) ? await this.makeCustomerPhoneTitle(item.contactPerson, item.customer, item.customerDesc) : this.makeTitleOfValue(item.customerDesc)));
                row.cells.push(new TableCell(this.makeTitleOfValue(item.statusDesc.replace(/ /g, '')), false));
                row.cells.push(new TableCell(this.makeTitleOfValue(item.title)));
                row.cells.push(new TableCell((this.phone) ? await this.makePhoneTitle(item.contactPerson, item.customer, item.createdBy) : this.makeTitleOfValue(item.createdBy)));
                if (this.recentlayout) {
                    row.cells.push(new TableCell(this.makeRecentItem(item.modifiedOn, item.modifiedBy, (<CompactRecentServiceCase>item).lastOpened)));
                    row.cells.push(new TableCell(''));
                    if (this.authorizationService.currentRoleIn(["SD", "FQS"])) {
                        row.cells.push(new TableCell(''));
                    }
                }
                if (!this.recentlayout) {
                    row.cells.push(new TableCell(this.makeRecentItem(item.modifiedOn, item.modifiedBy)));
                    row.cells.push(new TableCell(this.makeDeadlineIcon(item.requestedDeadline)));
                    if (this.authorizationService.currentRoleIn(["SD", "FQS"])) {
                        row.cells.push(new TableCell(this.makeRememberIcon(item.rememberMeDate, item.rememberMeReason)));
                    }
                }
                return row;
            }));
            if (this.onResultCountChanged) {
                this.onResultCountChanged({ resultcount: this.servicecases.length });
            }
        } else if (this.showemptyrow) {
            let row = new TableRow("empty", new Array<TableCell>());
            for (let i = 0; i < 9; i++) {
                row.cells.push(new TableCell("&nbsp;"));
            }
            let rows = [];
            rows.push(row);
            this.Rows = rows;
            if (this.onResultCountChanged) {
                this.onResultCountChanged({ resultcount: null });
            }
        } else {
            this.Rows = [];
            if (this.onResultCountChanged) {
                this.onResultCountChanged({ resultcount: null });
            }
        }
    }

    private getDateString(isoDate: ISODateTimeString): string {
        if (isoDate) {
            let today = new Date();
            today.setHours(0, 0, 0, 0);
            let yesterday = new Date();
            yesterday.setHours(0, 0, 0, 0);
            yesterday.setDate(yesterday.getDate() - 1);
            let timestamp = new Date(isoDate.toString());
            if (timestamp >= today)
                return this.$filter('date')(timestamp, 'HH:mm');
            else if (timestamp >= yesterday)
                return this.translationService.Translate('yesterday');
            else if (new Date().getFullYear() == timestamp.getFullYear())
                return this.$filter('date')(timestamp, 'd MMM');
            else
                return this.$filter('date')(timestamp, 'd MMM yyyy');
        }
    }

    private makeTitleOfValue(value: string, translate: boolean = true): string {

        if (translate) value = this.translationService.Translate(value);
        return `<span title="${value.replace(/"/g, "&quot;")}">${value}</span>`;
    }

    private async makeCustomerPhoneTitle(contact: IResourceResolver<Person>, customer: IResourceResolver<Customer>, name: string) {
        let title: string = ` title="${name}"`;
        if (customer) {
            let contactCustomer: Customer = await customer.value;
            if (contactCustomer.phone) {
                title = ` title="${name}: ${contactCustomer.phone}"`;
            } else {
                title = ` title="${name}"`;
            }
        }
        return `<span${title}>${name}</span>`;
    }

    private async makePhoneTitle(contact: IResourceResolver<Person>, customer: IResourceResolver<Customer>, name: string) {
        let title: string = ` title="${name}"`;
        if (contact) {
            let contactPerson: Person = await contact.value;
            if (contactPerson.phone && contactPerson.phone.length > 0) {
                title = ` title="${contactPerson.name}: ${contactPerson.phone.reverse().join(", ")}"`;
            } else {
                let contactCustomer: Customer = await customer.value;
                if (contactCustomer.phone) {
                    title = ` title="${contactCustomer.name}: ${contactCustomer.phone}"`;
                } else {
                    title = ` title="${contactPerson.name}"`;
                }
            }
        }
        return `<span${title}>${name}</span>`;
    }

    private static typeicons: { type: string, level: string, title?: string, titleadjective?: string, icon: string }[] = [
        { type: 'bug', level: 'level1', title: 'PROBLEM.LEVEL1', titleadjective: 'MTTR_HOURS', icon: 'icon-sev1' },
        { type: 'bug', level: 'level2', title: 'PROBLEM.LEVEL2', titleadjective: 'MTTR_DAYS', icon: 'icon-sev2' },
        { type: 'bug', level: 'level3', title: 'PROBLEM.LEVEL3', titleadjective: 'MTTR_WEEKS', icon: 'icon-sev3' },
        { type: 'bug', level: 'level4', title: 'PROBLEM.LEVEL4', titleadjective: 'MTTR_NEXT_RELEASE', icon: 'icon-sev4' },
        { type: 'bug', level: '', title: '', icon: 'icon-problem' },
        { type: 'request', level: '', title: 'REQUEST', icon: 'icon-request' }
    ];

    private makeTypeIcon(type: string, level: string): string {
        let typeicon = ServiceCaseTable.typeicons.filter(i => i.type == type && i.level == level).shift() || ServiceCaseTable.typeicons.filter(i => i.type == type).shift();
        if (typeicon) {
            let title = (typeicon.title ? this.translationService.Translate(typeicon.title) : "")
            let adjective = (typeicon.titleadjective ? " - " + this.translationService.Translate(typeicon.titleadjective) : "");
            return `<span title="${title}${adjective}" class="${typeicon.icon}"></span>`;
        }
        return "";
    }


    private makeRecentItem(modifiedOn: ISODateString, modifiedBy: string, lastOpened: ISODateString = null): string {
        let dateString: string;
        if (!modifiedOn) {
            return `<span></span>`;
        } else if (!modifiedBy) {
            let title = this.translationService.Translate('LAST_MODIFIED_{0}')
                .replace("{0}", new Date(modifiedOn).toLocaleString());
            dateString = this.getDateString((lastOpened) ? lastOpened : modifiedOn);
            return `<span title="${title}">${dateString}</span>`;
        } else {
            let title = this.translationService.Translate('LAST_MODIFIED_BY_{0}_{1}')
                .replace("{0}", modifiedBy)
                .replace("{1}", new Date(modifiedOn).toLocaleString());
            dateString = this.getDateString((lastOpened) ? lastOpened : modifiedOn);
            return `<span title="${title}">${dateString}</span>`;
        }
    }


    private makeDeadlineIcon(deadline: ISODateString): string {
        if (!deadline) {
            return `<span></span>`;
        } else {
            let diff = Date.diff(Date.today(), new Date(deadline));
            let text: string;

            if (diff.days > 1) {
                text = this.translationService.Translate("DEADLINE_FUTURE").replace("{0}", diff.days.toString());
            } else if (diff.days === 1) {
                text = this.translationService.Translate("DEADLINE_TOMORROW");
            } else if (diff.days === 0) {
                text = this.translationService.Translate("DEADLINE_TODAY");
            } else if (diff.days === -1) {
                text = this.translationService.Translate("DEADLINE_YESTERDAY");
            } else if (diff.days < -1) {
                text = this.translationService.Translate("DEADLINE_PAST").replace("{0}", (-diff.days).toString());
            }

            let deadlineClass: string;
            if (diff.days < 0) {
                deadlineClass = "error";
            } else if (diff.days < 5) {
                deadlineClass = "warning";
            } else {
                deadlineClass = "information";
            }

            return `<span title="${text}" class="icon-deadline ${deadlineClass}"></span>`;
        }
    }


    private makeRememberIcon(remember: ISODateString, reason: string): string {
        if (!remember) {
            return `<span></span>`;
        } else {
            let diff = Date.diff(Date.today(), new Date(remember));
            let text: string;

            if (diff.days > 1) {
                text = this.translationService.Translate("REMEMBER_FUTURE").replace("{datediff}", diff.days.toString()).replace("{reason}", reason);
            } else if (diff.days === 1) {
                text = this.translationService.Translate("REMEMBER_TOMORROW").replace("{reason}", reason);
            } else if (diff.days === 0) {
                text = this.translationService.Translate("REMEMBER_TODAY").replace("{reason}", reason);
            } else if (diff.days === -1) {
                text = this.translationService.Translate("REMEMBER_YESTERDAY").replace("{reason}", reason);
            } else if (diff.days < -1) {
                text = this.translationService.Translate("REMEMBER_PAST").replace("{datediff}", (-diff.days).toString()).replace("{reason}", reason);
            }

            let rememberClass: string;
            if (diff.days < 0) {
                rememberClass = "error";
            } else if (diff.days < 5) {
                rememberClass = "warning";
            } else {
                rememberClass = "information";
            }

            return `<span title="${text}" class="icon-reminder ${rememberClass}"></span>`;
        }
    }

    private priorityicons = [
        { type: 'high', title: 'HIGH', icon: 'icon-prioHIGH' },
        { type: 'medium', title: 'MEDIUM', icon: 'icon-prioMEDIUM' },
        { type: 'low', title: 'LOW', icon: 'icon-prioLOW' },
    ];

    private makePriorityIcon(type: string): string {
        let priority = this.priorityicons.filter(p => p.type == type).shift();
        if (priority) {
            let title = (priority.title ? this.translationService.Translate(priority.title) : "");
            return `<span title="${title}" class="${priority.icon}"></span>`;
        }
        return "";
    }

    private severityicons = [
        { type: 'level1', title: 'level1', icon: 'icon-sev1' },
        { type: 'level2', title: 'level2', icon: 'icon-sev2' },
    ];
    private makeSeverityIcon(type: string): string {
        let severity = this.severityicons.filter(s => s.type == type).shift();
        if (severity)
            return `<span title="${severity.title}" class="${severity.icon}"></span>`;
        return "";
    }
    private makeActionIcon(required: boolean): string {
        if (!required)
            return "";
        return `<span title="${this.translationService.Translate("ACTION_REQUIRED")}" class="icon icon-A"></span>`;
    }
    private makeFeedbackIcon(feedbackStatus: FeedbackStatus): string {
        if (feedbackStatus === "requested")
            return `<span title="${this.translationService.Translate("FEEDBACK_REQUIRED")}" class="icon icon-F"></span>`;
        return "";
    }

    private ondataresize(count: number) {
    }
}

